import { useAddToWishlistAction } from '@/hooks/actions/use-add-to-wishlist-action';
import { useRemoveFromWishlistAction } from '@/hooks/actions/use-remove-from-wishlist-action';
import { useBatchWishlistCheckQuery } from '@/hooks/queries/use-batch-wishlist-check-query';
import type { WishlistCreator } from '@fluenceur/types';
import { useCallback } from 'react';

interface CreatorData {
  platformUserId?: string | null;
  sourceCreatorId?: string | null;
  platform?: string;
  /** Absent on a prospect the plan masks: it is saved by `sourceCreatorId`. */
  username?: string;
  fullName?: string | null;
  /**
   * Email read from a creator-api surface (discovery grid/drawer,
   * recommendations). NEVER a brand-typed address — hand-typed emails flow
   * through the wishlist row edit / contact override instead. The add payload
   * flags it `emailProvenance: 'discovery'` so the backend routes it to the
   * global enrichment cache, not a brand-private override.
   */
  email?: string | null;
  profilePictureUrl?: string | null;
  followersCount?: number | null;
  category?: string | null;
}

interface UseWishlistToggleOptions {
  /** Pre-checked wishlist status (from batch check in parent) */
  isInWishlist?: boolean;
  /** Pre-checked wishlist item (from batch check in parent) */
  wishlistItem?: WishlistCreator | null;
  /** Callback when toggle succeeds */
  onSuccess?: () => void;
  /** Recommendation id when saving from an agent recommendation row. */
  recommendationId?: string;
}

/**
 * Hook to manage wishlist toggle for a creator.
 *
 * Supports two modes:
 * 1. Pre-checked mode: When isInWishlist/wishlistItem are provided (from batch check)
 * 2. Self-fetch mode: When no pre-checked data, fetches individually
 *
 * @param creator - Creator data (null if not loaded yet)
 * @param options - Optional pre-checked data and callbacks
 * @returns { isInWishlist, toggle, isToggling, isLoading }
 */
export const useWishlistToggle = (
  creator: CreatorData | null,
  options: UseWishlistToggleOptions = {}
) => {
  const {
    isInWishlist: preCheckedIsInWishlist,
    wishlistItem: preCheckedWishlistItem,
    onSuccess,
    recommendationId,
  } = options;

  // Self-fetch mode: when no pre-checked data is provided
  const shouldFetch =
    creator &&
    preCheckedIsInWishlist === undefined &&
    (!!creator.platformUserId || !!creator.sourceCreatorId);
  const platform = creator?.platform ?? 'instagram';

  const {
    data: fetchedData,
    refetch,
    isFetching,
  } = useBatchWishlistCheckQuery(
    shouldFetch && creator?.platformUserId ? [creator.platformUserId] : [],
    platform,
    !!shouldFetch,
    shouldFetch && creator?.sourceCreatorId ? [creator.sourceCreatorId] : []
  );

  // Use pre-checked data if available, otherwise use fetched data
  const fetchedWishlistItem =
    creator && fetchedData
      ? ((creator.sourceCreatorId ? fetchedData[creator.sourceCreatorId] : undefined) ??
        (creator.platformUserId ? fetchedData[creator.platformUserId] : undefined) ??
        null)
      : null;
  const wishlistItem = preCheckedWishlistItem ?? fetchedWishlistItem ?? null;
  const isInWishlist = preCheckedIsInWishlist ?? !!fetchedWishlistItem;

  const addToWishlist = useAddToWishlistAction();
  const removeFromWishlist = useRemoveFromWishlistAction();
  const isToggling = addToWishlist.isPending || removeFromWishlist.isPending;
  const isLoading = isFetching && !!shouldFetch;

  const toggle = useCallback(() => {
    if (isToggling || !creator || (!creator.platformUserId && !creator.sourceCreatorId)) return;

    const handleSuccess = () => {
      // Refetch only in self-fetch mode
      if (shouldFetch) refetch();
      onSuccess?.();
    };
    if (isInWishlist && wishlistItem) {
      removeFromWishlist.mutate(wishlistItem.id, { onSuccess: handleSuccess });
    } else {
      addToWishlist.mutate(
        {
          platformUserId: creator.platformUserId ?? null,
          sourceCreatorId: creator.sourceCreatorId ?? null,
          platform,
          ...(creator.username && { username: creator.username }),
          fullName: creator.fullName,
          email: creator.email,
          // Emails reaching this hook always come from creator-api data (see
          // CreatorData.email) — mark the provenance so the backend enriches the
          // GLOBAL identity instead of writing a brand-private override.
          emailProvenance: creator.email ? 'discovery' : undefined,
          profilePictureUrl: creator.profilePictureUrl,
          followersCount: creator.followersCount,
          category: creator.category,
          recommendationId,
        },
        { onSuccess: handleSuccess }
      );
    }
  }, [
    isToggling,
    creator,
    isInWishlist,
    wishlistItem,
    shouldFetch,
    refetch,
    onSuccess,
    platform,
    recommendationId,
    addToWishlist,
    removeFromWishlist,
  ]);

  return { isInWishlist, wishlistItem, toggle, isToggling, isLoading };
};
